import React, { useEffect } from 'react';

import { Card, CardSection } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { decodeHtml } from '../helpers/utils';
import { useCapitalTracking } from 'root/apps/CapitalBanner/hooks/';
import { VisibilityTracker } from 'root/apps/tracking/VisibilityTracker';
import { datadogRum } from 'root/datadog/datadogRum';

import { getTrackingEnvironment } from '../tracking/getTrackingEnvironment';
import { TrackingProvider } from '../tracking/TrackingContext';
import TrackingIdentify from '../tracking/TrackingIdentify';
import { FlameButton, FlameSecondaryButton, Grid, HidingBox } from './BusinessSnapshot.style';
import {
  CapitalOffer,
  DismissalEvent,
  DismissalType,
  SnapshotDismissal,
} from './BusinessSnapshot.types';
import { BusinessSnapshotTrackingEvents } from './enums';
import SnapshotAside from './SnapshotAside';
import { ChartType } from './useSnapshotChartData';

const prefType = 'reports';
const prefComponent = 'business_snapshot';
const prefName = 'dismissal';

declare global {
  interface Window {
    rearmSnapshotBanner: () => string;
  }
}

// Create global rearm banner function for testing, no localization needed
window.rearmSnapshotBanner = () => {
  window.lsCloud.UserPreferences.save(prefType, prefComponent, prefName, 'false');
  return 'Please refresh the page to see the banner again.';
};

export type BusinessSnapshotProps = {
  dismissalValue?: string;
  offer?: CapitalOffer;
  chartType: ChartType;
};

function BusinessSnapshot({ dismissalValue, offer, chartType }: BusinessSnapshotProps) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [userDismissed, setUserDismissed] = React.useState<DismissalType>('none');
  const [hidden, setHidden] = React.useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const bannerLocation = 'reports-top-left';
  const bannerVersion = 'business-snapshot-cta-v1';
  const dismissalType = 'never';
  const placement = 'business-snapshot-cta-v1';
  const source = 'reports';
  const { trackCapitalEvent } = useCapitalTracking({ source });

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!offer?.offerAmount) return null;

  // We handle the html decode client side to prevent any HTML injection
  let dismissal: SnapshotDismissal | null = null;
  try {
    dismissal = JSON.parse(decodeHtml(dismissalValue));
  } catch {
    // If the parse fails, we'll get a new dismissal object
    // when the user dismisses the banner
  }

  if (dismissal) {
    const convertedDate = new Date(dismissal.dismissedAt);
    const dismissedAtDate = isNaN(convertedDate.getTime()) ? null : convertedDate;

    // We use .now() to allow for mocking in tests
    const now = new Date(Date.now());

    const fourteenDaysAgo = new Date(now);
    fourteenDaysAgo.setDate(now.getDate() - 14);

    if (dismissedAtDate) {
      switch (dismissal.dismissedType) {
        case 'banner':
          if (dismissedAtDate >= fourteenDaysAgo && dismissedAtDate.getMonth() === now.getMonth()) {
            return null;
          }
          break;

        case 'days':
          if (dismissedAtDate >= fourteenDaysAgo) {
            return null;
          }
          break;

        case 'month':
          if (dismissedAtDate.getMonth() === now.getMonth()) {
            return null;
          }
          break;

        case 'offer':
          if (dismissal.previousOffer === offer.offerAmount) {
            return null;
          }
          break;

        default:
          break;
      }
    }
  }

  const handleDismiss = (
    dismissedType: DismissalType = 'banner',
    dismissedEvent: DismissalEvent = 'aside'
  ) => {
    const dismissal: SnapshotDismissal = {
      dismissedAt: new Date(Date.now()).toISOString(),
      dismissedType,
      previousOffer: offer.offerAmount,
    };

    // We don't want to wait on the pref save, so we fire and forget
    window.lsCloud.UserPreferences.save(
      prefType,
      prefComponent,
      prefName,
      JSON.stringify(dismissal)
    );

    // First show the "we'll check in" message
    setUserDismissed(dismissedType);

    // Fully hide banner after ten seconds
    timerRef.current = setTimeout(() => {
      setHidden(true);
      if (dismissedEvent === 'click') {
        trackCapitalEvent(BusinessSnapshotTrackingEvents.BusinessSnapshotCtaClicked, {
          banner_location: bannerLocation,
          banner_version: bannerVersion,
          button_type: 'dismiss',
          dismissal_type: dismissalType,
        });
      }
    }, 10000);
  };

  const handleAsideClose = (dismissalType: DismissalType) => {
    setIsModalOpen(false);
    if (dismissalType === 'none') return;
    handleDismiss(dismissalType);
  };

  const handleClick = () => {
    setIsModalOpen(true);
    timerRef.current = setTimeout(() => {
      trackCapitalEvent(BusinessSnapshotTrackingEvents.BusinessSnapshotCtaClicked, {
        banner_location: bannerLocation,
        banner_version: bannerVersion,
        button_type: 'view',
      });
    }, 10000);
  };

  if (hidden) {
    return null;
  }

  const businessSnapshot = () => (
    <Card>
      <CardSection pt="15px" pb="24px" pl="24px" pr="24px">
        <Grid>
          <Flex justifyContent="center" alignItems="center" backgroundColor="#fff">
            <Text style={{ color: '#000', textAlign: 'center' }}>
              {(userDismissed === 'banner' || userDismissed === 'days') && (
                <>We&apos;ll check back in a few days with the Business Snapshot.</>
              )}
              {userDismissed === 'month' && (
                <>We&apos;ll check back in a month with your next Business Snapshot.</>
              )}
              {userDismissed === 'offer' && (
                <>
                  We&apos;ll check back once your offer amount has changed with a new Business
                  Snapshot.
                </>
              )}
            </Text>
          </Flex>
          <Flex
            justifyContent="space-between"
            minHeight="136px"
            backgroundColor="#fff"
            style={{ visibility: userDismissed !== 'none' ? 'hidden' : 'visible' }}
          >
            <Flex justifyContent="space-between" flexDirection="column">
              <Box>
                <Text fontSize="18px" lineHeight="36px" fontWeight={700} style={{ color: '#000' }}>
                  Your {Intl.DateTimeFormat(navigator.language, { month: 'long' }).format()}{' '}
                  Business Snapshot has arrived!
                </Text>
                <Text fontSize="14px" lineHeight="18px">
                  View data-backed recommendations made for your business needs.
                </Text>
              </Box>
              <Box mt={18}>
                <FlameButton fill onClick={() => handleDismiss('banner', 'click')}>
                  Maybe later
                </FlameButton>
                <FlameSecondaryButton variant="secondary" fill onClick={handleClick}>
                  View my Snapshot
                </FlameSecondaryButton>
              </Box>
            </Flex>
            <HidingBox>
              <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/components/business_snapshot/snapshot_rocket.png" alt="rocket" />
            </HidingBox>
          </Flex>
        </Grid>
      </CardSection>
      {isModalOpen && (
        <SnapshotAside
          isModalOpen={isModalOpen}
          offer={offer}
          onRequestClose={handleAsideClose}
          chartType={chartType}
        />
      )}
    </Card>
  );

  return (
    <TrackingProvider
      configs={[{ name: 'product', environment: getTrackingEnvironment() }]}
      fallbackRender={businessSnapshot}
      onInitError={(err) => datadogRum.addError(err, { context: 'tracking_module_init' })}
    >
      <TrackingIdentify />
      <VisibilityTracker
        id={placement}
        eventName={BusinessSnapshotTrackingEvents.BusinessSnapshotCtaViewed}
        triggerOnce={true}
        eventData={{
          source,
          banner_location: bannerLocation,
          banner_version: bannerVersion,
        }}
      >
        {businessSnapshot()}
      </VisibilityTracker>
    </TrackingProvider>
  );
}

export default BusinessSnapshot;
